import { useCallback, useEffect, useMemo, useState } from 'react';

import { isNegotiatedQuote } from '~/util/types/isNegotiatedQuote';

import { isNegotiationCaseAwardable } from './DemandAwardingWidget.awardability';
import { Demand, DemandNegotiationCase, DemandNegotiationCaseQuoteItem } from './DemandAwardingWidget.types';

export interface LineItemNegotiationCaseAssignment {
  [lineItemId: string]: string | undefined;
}

type DemandRequestLineItem = NonNullable<NonNullable<Demand['demandRequest']>['lineItems'][number]>;
type DemandNegotiatedQuoteItem = DemandNegotiationCaseQuoteItem;
type AssignableQuoteItem = Pick<DemandNegotiatedQuoteItem, 'demandRequestItemId' | 'isAwardable'>;

const getQuoteLineItems = (quote: DemandNegotiationCase['quote'] | null | undefined): Record<string, unknown>[] => {
  if (
    typeof quote === 'object' &&
    quote !== null &&
    'lineItems' in quote &&
    Array.isArray((quote as { lineItems?: unknown }).lineItems)
  ) {
    return quote.lineItems as Record<string, unknown>[];
  }

  return [];
};

const getAssignableQuoteItemsByNegotiationCase = (
  negotiationCase: DemandNegotiationCase,
  demandRequestLineItems: DemandRequestLineItem[],
  awardableConfirmedQuotes: boolean,
): AssignableQuoteItem[] => {
  if (!negotiationCase.quote) {
    return [];
  }

  const isAwardingActive = isNegotiationCaseAwardable({
    status: negotiationCase.status,
    isAwardable: negotiationCase.isAwardable,
    awardableConfirmedQuotes,
  });
  if (!isAwardingActive) {
    return [];
  }

  if (isNegotiatedQuote(negotiationCase.quote)) {
    return getQuoteLineItems(negotiationCase.quote).reduce((acc, quoteItem) => {
      const demandRequestItemId =
        'demandRequestItemId' in quoteItem ? (quoteItem.demandRequestItemId as string | null | undefined) : undefined;
      const isAwardable = !('isAwardable' in quoteItem) || Boolean(quoteItem.isAwardable as boolean | null | undefined);

      if (isAwardable && demandRequestItemId) {
        acc.push({ demandRequestItemId, isAwardable: true });
      }

      return acc;
    }, [] as AssignableQuoteItem[]);
  }

  // Fallback for awardable non-negotiated quotes:
  // map quote line items to demand request items by row index.
  return getQuoteLineItems(negotiationCase.quote).reduce((acc, _quoteItem, index) => {
    const demandRequestItemId = demandRequestLineItems[index]?.id;
    if (demandRequestItemId) {
      acc.push({ demandRequestItemId, isAwardable: true });
    }
    return acc;
  }, [] as AssignableQuoteItem[]);
};

export interface LineItemNegotiationCaseAssignments {
  value: LineItemNegotiationCaseAssignment | undefined;
  resetLineItemAssignments: () => void;
  toggleLineItemAssignment: (lineItemId: string, negotiationCase: DemandNegotiationCase) => void;
  assignAllLineItemsToNegotiationCase: (negotiationCase: DemandNegotiationCase) => void;
  areAllLineItemsAssignedToNegotiationCase: (negotiationCase: DemandNegotiationCase) => boolean;
  isLineItemAssignedToNegotiationCase: (lineItemId: string, negotiationCase: DemandNegotiationCase) => boolean;
  isAtLeastOneLineItemAssigned: boolean;
  hasAssignableQuoteItems: boolean;
  hasAssignableQuoteItemsForNegotiationCase: (negotiationCase: DemandNegotiationCase) => boolean;
  isAwardingActiveForNegotiationCase: (negotiationCase: DemandNegotiationCase) => boolean;
}

/**
 * State and helper methods for assigning demand request line items to negotiation cases.
 */
export const useLineItemAssignments = (
  demand: Demand | undefined,
  awardableConfirmedQuotes: boolean,
): LineItemNegotiationCaseAssignments => {
  const [value, setValue] = useState<LineItemNegotiationCaseAssignment | undefined>();

  const { demandRequest, negotiationCases } = demand || {};
  const demandRequestLineItems = useMemo(() => demandRequest?.lineItems ?? [], [demandRequest]);
  const negotiationCasesNormalized = negotiationCases?.nodes ?? [];

  /**
   * Shared reset builder used by effect-initialization and manual reset.
   * Memoized so hook dependency arrays stay stable and lint-safe.
   */
  const getEmptySelectionState = useCallback(() => {
    return demandRequestLineItems.reduce((acc, lineItem) => {
      acc[lineItem.id] = undefined;
      return acc;
    }, {} as LineItemNegotiationCaseAssignment);
  }, [demandRequestLineItems]);

  useEffect(() => {
    setValue(getEmptySelectionState());
  }, [getEmptySelectionState]);

  const assignableQuoteItemsByNegotiationCase: Record<string, AssignableQuoteItem[]> = {};
  negotiationCasesNormalized.forEach((negotiationCase) => {
    assignableQuoteItemsByNegotiationCase[negotiationCase.id] = getAssignableQuoteItemsByNegotiationCase(
      negotiationCase,
      demandRequestLineItems,
      awardableConfirmedQuotes,
    );
  });

  // Provide a null-object if data is not ready or state has not been initialized yet
  if (!value || negotiationCasesNormalized.length === 0) {
    return {
      value,
      resetLineItemAssignments: () => {},
      toggleLineItemAssignment: () => {},
      assignAllLineItemsToNegotiationCase: () => {},
      areAllLineItemsAssignedToNegotiationCase: () => false,
      isLineItemAssignedToNegotiationCase: () => false,
      isAtLeastOneLineItemAssigned: false,
      hasAssignableQuoteItems: false,
      hasAssignableQuoteItemsForNegotiationCase: () => false,
      isAwardingActiveForNegotiationCase: () => false,
    };
  }
  return {
    value,
    resetLineItemAssignments: () => {
      // Reset the line item assignments to the empty state
      setValue(getEmptySelectionState());
    },
    toggleLineItemAssignment: (lineItemId: string, negotiationCase: DemandNegotiationCase) => {
      if (value[lineItemId] === negotiationCase.id) {
        setValue((prev) => ({
          ...prev,
          [lineItemId]: undefined,
        }));
        return;
      }

      setValue((prev) => ({
        ...prev,
        [lineItemId]: negotiationCase.id,
      }));
    },
    assignAllLineItemsToNegotiationCase: (negotiationCase: DemandNegotiationCase) => {
      const selectableQuoteItems = assignableQuoteItemsByNegotiationCase[negotiationCase.id] ?? [];
      const newSelectionState = selectableQuoteItems.reduce((acc, quoteItem) => {
        const demandRequestItemId = quoteItem.demandRequestItemId;
        if (demandRequestItemId) {
          acc[demandRequestItemId] = negotiationCase.id;
        }
        return acc;
      }, {} as LineItemNegotiationCaseAssignment);

      setValue({ ...value, ...newSelectionState });
    },
    areAllLineItemsAssignedToNegotiationCase: (negotiationCase: DemandNegotiationCase) => {
      const selectableQuoteItems = assignableQuoteItemsByNegotiationCase[negotiationCase.id] ?? [];

      return (
        selectableQuoteItems.length > 0 &&
        selectableQuoteItems.every((quoteItem) => {
          const demandRequestItemId = quoteItem.demandRequestItemId;
          return demandRequestItemId ? value[demandRequestItemId] === negotiationCase.id : false;
        })
      );
    },
    isLineItemAssignedToNegotiationCase: (lineItemId: string, negotiationCase: DemandNegotiationCase) => {
      return value[lineItemId] === negotiationCase.id;
    },
    isAtLeastOneLineItemAssigned: (() => {
      const selectableLineItems = Object.keys(value).filter((lineItemId) => {
        return negotiationCasesNormalized.some((negotiationCase) => {
          return (assignableQuoteItemsByNegotiationCase[negotiationCase.id] ?? []).some(
            (quoteItem) => quoteItem.demandRequestItemId === lineItemId,
          );
        });
      });

      return (
        selectableLineItems.length > 0 && selectableLineItems.some((lineItemId) => value[lineItemId] !== undefined)
      );
    })(),
    hasAssignableQuoteItems: negotiationCasesNormalized.some(
      (negotiationCase) => (assignableQuoteItemsByNegotiationCase[negotiationCase.id] ?? []).length > 0,
    ),
    hasAssignableQuoteItemsForNegotiationCase: (negotiationCase: DemandNegotiationCase) => {
      return (assignableQuoteItemsByNegotiationCase[negotiationCase.id] ?? []).length > 0;
    },
    isAwardingActiveForNegotiationCase: (negotiationCase: DemandNegotiationCase) => {
      return isNegotiationCaseAwardable({
        status: negotiationCase.status,
        isAwardable: negotiationCase.isAwardable,
        awardableConfirmedQuotes,
      });
    },
  };
};

export type { DemandRequestLineItem, DemandNegotiatedQuoteItem };
